import { useCallback, useEffect, useRef, useState } from 'react';
import { registerNewbuildingBrokerModalsMicroFrontendManifest } from 'shared/services/registerNewbuildingBrokerModalsMicroFrontendManifest';
import { INewbuildingBrokerModalsMicroFrontendApi, IOpenDIYModalParams } from 'shared/types/microfrontends';
import { useApplicationContext } from 'shared/utils/applicationContext';

import type { TOpenMicrofrontResultHandler } from '../types';
import type { ICianMFUIElement } from '@cian/mf-registry/browser';
import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

export function useMicrofrontAPI(resultCallback: TOpenMicrofrontResultHandler) {
  const { httpApi, logger, microfrontendsRegistry } = useApplicationContext();

  const microfrontRef = useRef<ICianMFUIElement>(null);
  const [microfrontManifest, setMicrofrontManifest] = useState<IMicrofrontendManifest>();

  const getLocalAPI = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    (): INewbuildingBrokerModalsMicroFrontendApi => (microfrontRef.current?.instance?.customElement as any)?.api,
    [microfrontRef],
  );

  const paramsRef = useRef<IOpenDIYModalParams | undefined>(undefined);
  const firstAPICallRef = useRef(true);

  useEffect(() => {
    // Логика выполняется один раз при первом вызове локального API после установки манифеста в стейт.
    if (microfrontManifest && firstAPICallRef.current) {
      firstAPICallRef.current = false;

      // Хак, чтобы подождать рендера `<Microfrontend />` и инициализации рефа для локального API.
      setTimeout(() => {
        const api = getLocalAPI();

        if (api) {
          api['/v1/open-self-request-modal/'](paramsRef.current as IOpenDIYModalParams);
          resultCallback({ isError: false });
        } else {
          resultCallback({ isError: true });
        }
      }, 0);
    }
  }, [resultCallback, getLocalAPI, microfrontManifest]);

  const registerAndSetManifest = useCallback(
    async (params: IOpenDIYModalParams) => {
      const result = await registerNewbuildingBrokerModalsMicroFrontendManifest({
        httpApi,
        logger,
        microfrontendsRegistry,
      });

      if (result) {
        // Сохраняем в реф, чтобы вызвать с этими параметрами после установки в стейт и инициализации рефа.
        paramsRef.current = params;

        setMicrofrontManifest(result);
      } else {
        resultCallback({ isError: true });
      }
    },
    [httpApi, logger, microfrontendsRegistry, resultCallback],
  );

  const openMicrofront = useCallback(
    async (params: IOpenDIYModalParams): Promise<void> => {
      if (microfrontManifest) {
        getLocalAPI()['/v1/open-self-request-modal/'](params);
        resultCallback({ isError: false });
      } else {
        await registerAndSetManifest(params);
      }
    },
    [getLocalAPI, registerAndSetManifest, resultCallback, microfrontManifest],
  );

  return {
    microfrontManifest,
    microfrontRef,
    openMicrofront,
  };
}
